/**
 * Logotipo de la marca.
 *
 * El logo real del negocio (los letreros de madera, la lona del changarro)
 * es deliberadamente informal: es publicidad de calle. Aquí se necesita la
 * misma identidad —el nombre "Erimar" y su origen en Suc-Tuc, Hopelchén—
 * pero leída en una pantalla: login del dueño y menú del comensal. Por eso
 * es tipografía, no una foto del letrero: un serif itálico para el nombre y
 * mayúsculas espaciadas para el resto, que es el registro visual que en
 * cualquier fuente se lee como "elegante" en vez de "changarro".
 *
 * `variant="login"` apila todo al centro, grande, para la pantalla de
 * acceso. `variant="compacta"` lo acomoda en una sola línea para caber en
 * la cabecera del menú QR sin empujar el resto del encabezado.
 */
interface Props {
  variant?: 'login' | 'compacta';
  className?: string;
}

export function LogoErimar({ variant = 'login', className = '' }: Props) {
  if (variant === 'compacta') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <HojaIcono className="h-5 w-5 shrink-0 text-nopal" />
        <div className="leading-none">
          <span className="font-marca text-xl italic leading-none text-nopal-oscuro">Erimar</span>
          <span className="mt-0.5 block font-dato text-[9px] uppercase tracking-[.18em] text-salvia">
            Suc-Tuc · Hopelchén
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center text-center ${className}`}>
      <span className="font-dato text-[11px] font-semibold uppercase tracking-[.35em] text-salvia">
        Antojitos
      </span>
      <div className="mt-1 flex items-center gap-2.5">
        <HojaIcono className="h-8 w-8 shrink-0 text-nopal" />
        <h1 className="font-marca text-5xl italic leading-none text-nopal-oscuro">Erimar</h1>
        <HojaIcono className="h-8 w-8 shrink-0 -scale-x-100 text-nopal" />
      </div>
      <span className="mt-2.5 font-dato text-[11px] uppercase tracking-[.3em] text-salvia">
        Suc-Tuc, Hopelchén
      </span>
    </div>
  );
}

/** Una hoja simple: el único adorno del logo real que sobrevive a la versión tipográfica. */
function HojaIcono({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M4 20C4 11 10 4 20 4C20 14 13 20 4 20Z"
        fill="currentColor"
        fillOpacity="0.15"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M5.5 18.5C9 14.5 12.5 11 18 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
